import React, { useEffect, useRef, useState, useContext } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  GoogleMap,
  Polygon,
  StandaloneSearchBox,
  useJsApiLoader,
  Marker,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiTrash2, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { HiOutlineArrowLeft } from "react-icons/hi";
import FloatingInput from "../../../Components/Common/FloatingInput";
import * as routes from "../../../routes";
import { Loader } from "../../../Components/Common";
import { UserContext } from "../../../Context";
import * as constants from "../constants";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { USER_ROLES } from "../../../Constants/Strings";

const containerStyle = { width: "100%", height: "clamp(320px, 42vw, 520px)" };
const center = { lat: 18.5204, lng: 73.8567 }; // Pune city center
const libraries = ["places"];

const validationSchema = Yup.object().shape({
  name: Yup.string().required(constants.SITE_NAME_REQUIRED),
  client_name: Yup.string().required(constants.CLIENT_NAME_REQUIRED),
  client_email: Yup.string()
    .email(constants.INVALID_MAIL)
    .required(constants.CLIENT_MAIL_REQUIRED),
  client_phone: Yup.string()
    .matches(/^[0-9]{10}$/, constants.MOBILE_VALIDATION)
    .required(constants.MOBILE_REQUIRED),
  client_address: Yup.string().required(constants.CLIENT_ADDRESS_REQUIRED),
  estimated_start_date: Yup.date().required(constants.START_DATE),
  estimated_end_date: Yup.date()
    .required(constants.END_DATE)
    .min(Yup.ref("estimated_start_date"), constants.DATE_VALIDATION),
  site_incharge: Yup.string().required(constants.SITE_INCHARGE_REQUIRED),
  project_incharge: Yup.string().required(constants.PROJECT_INCHARGE_REQUIRED),
  is_active: Yup.boolean(),
});

const SiteForm = ({
  mode = constants.ADD_MODE,
  initialValues = {},
  onSubmit,
  submitText = "Save Site",
  loading,
  siteInchargeList = [],
  projectInchargeList = [],
  tagOptionsList = [],
  allUsersList = [],
}) => {
  const [polygonData, setPolygonData] = useState(null);
  const [polygonPath, setPolygonPath] = useState([]);
  const [sumbmitting, isSubmitting] = useState(false);
  const [map, setMap] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [showAllSelected, setShowAllSelected] = useState(false);
  const SELECTED_USERS_PREVIEW_COUNT = 5;
  const searchBoxRef = useRef(null);
  const navigate = useNavigate();
  const { isAdminOrHO, user } = useContext(UserContext);
  const isHR = user?.role === USER_ROLES.HR;
  const canEditSite = isAdminOrHO || isHR;

  const isViewMode = mode === constants.VIEW_MODE;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (!initialValues?.geofence) return;

    try {
      let coordinates;

      if (typeof initialValues?.geofence === "string") {
        const matches = initialValues?.geofence.match(/\(\((.*?)\)\)/);
        if (matches && matches[1]) {
          const points = matches[1].split(",").map((pair) => {
            const [lng, lat] = pair.trim().split(" ").map(Number);
            return { lat, lng };
          });

          if (
            points.length > 1 &&
            points[0].lat === points[points.length - 1].lat &&
            points[0].lng === points[points.length - 1].lng
          ) {
            points.pop();
          }
          setPolygonPath(points);
        }
      } else if (typeof initialValues?.geofence === "object") {
        coordinates = initialValues?.geofence?.coordinates?.[0];
        if (coordinates) {
          const path = coordinates.map(([lng, lat]) => ({ lat, lng }));
          if (
            path.length > 1 &&
            path[0].lat === path[path.length - 1].lat &&
            path[0].lng === path[path.length - 1].lng
          ) {
            path.pop();
          }
          setPolygonPath(path);
        }
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || constants.GEOFENCE_ERROR;
      toast.error(msg);
    }
  }, [initialValues?.geofence]);

  useEffect(() => {
    if (polygonPath.length > 2) {
      const geojson = {
        type: "Polygon",
        coordinates: [[polygonPath.map((p) => [p.lng, p.lat])]],
      };
      setPolygonData(JSON.stringify(geojson));
    }
  }, [polygonPath]);

  const getUserInitials = (u) =>
    `${u?.first_name?.[0] || ""}${u?.last_name?.[0] || ""}`.toUpperCase();

  const filteredUsersList = allUsersList.filter((u) =>
    `${u.first_name} ${u.last_name}`
      .toLowerCase()
      .includes(userSearch.trim().toLowerCase())
  );

  const polygonPathToWKT = (path) => {
    if (path.length < 3) return null;
    const coordinates = [...path, path[0]]; // close polygon
    return `POLYGON((${coordinates
      .map((p) => `${p.lng} ${p.lat}`)
      .join(", ")}))`;
  };

  const handlePlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places.length > 0) {
      const location = places[0].geometry.location;
      map.panTo({ lat: location.lat(), lng: location.lng() });
      map.setZoom(16);
    }
  };

  const formInitialValues = {
    name: initialValues?.name || "",
    client_name: initialValues?.client?.name || "",
    client_email: initialValues?.client?.email || "",
    client_phone: initialValues?.client?.phone || "",
    client_address: initialValues?.client?.address || "",
    estimated_start_date: initialValues?.estimated_start_date || "",
    estimated_end_date: initialValues?.estimated_end_date || "",
    site_incharge: initialValues?.site_incharge || "",
    project_incharge: initialValues?.project_incharge || "",
    is_active: initialValues?.is_active ?? true,
    tag_option: initialValues?.tag_option || "",
    assigned_users: (initialValues?.assigned_users_info || []).map((u) => u.id),
  };

  const handleFormSubmit = async (values, helpers) => {
    isSubmitting(true);
    if (!polygonData && mode === constants.ADD_MODE) {
      toast.error(constants.GEOFENCE_VALIDATION);
      return;
    }

    let finalGeofence = null;
    if (polygonPath.length > 2) {
      finalGeofence = polygonPathToWKT(polygonPath);
    }

    const payload = {
      name: values.name,
      client: {
        name: values.client_name,
        email: values.client_email,
        phone: values.client_phone,
        address: values.client_address,
      },
      estimated_start_date: values.estimated_start_date,
      estimated_end_date: values.estimated_end_date,
      geofence: finalGeofence || polygonData || initialValues?.geofence,
      site_incharge: values.site_incharge,
      project_incharge: values.project_incharge,
      is_active: values.is_active,
      tag_option: values.tag_option || null,
      assigned_users: values.assigned_users,
    };

    await onSubmit(payload, helpers);
  };

  return (
    <div className="max-w-3xl mx-auto mt-2 p-4 bg-white rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <button
          type="button"
          onClick={() => navigate(routes.SITE)}
          className="text-primary hover:text-secondary text-2xl"
          title="Back"
        >
          <HiOutlineArrowLeft />
        </button>

        <h2 className="text-xl font-bold text-primary text-center flex-1">
          {mode === constants.ADD_MODE
            ? "Create New Site"
            : mode === constants.EDIT_MODE
            ? "Edit Site"
            : "View Site"}
        </h2>

        {mode === constants.VIEW_MODE ? (
          canEditSite && (
            <button
              type="button"
              onClick={() =>
                navigate(`${routes.SITE}/edit/${initialValues?.id}`)
              }
              className="bg-primary hover:bg-secondary text-white px-4 py-2 rounded font-semibold"
            >
              ✎ Edit
            </button>
          )
        ) : (
          <div className="w-6" />
        )}
      </div>

      {!isLoaded || loadError || loading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <Loader />
        </div>
      ) : (
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          enableReinitialize
          onSubmit={handleFormSubmit}
          className="relative z-0"
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="flex flex-col lg:flex-row gap-4 items-start">
                <div className="flex-1 min-w-0 w-full">
                  <FloatingInput
                    name="name"
                    label="Site Name *"
                    disabled={isViewMode || sumbmitting}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FloatingInput
                      name="client_name"
                      label="Client Name *"
                      disabled={isViewMode || sumbmitting}
                    />
                    <FloatingInput
                      name="client_email"
                      label="Client Email *"
                      type="email"
                      disabled={isViewMode || sumbmitting}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FloatingInput
                      name="client_phone"
                      label="Client Phone *"
                      disabled={isViewMode || sumbmitting}
                    />
                    <FloatingInput
                      name="client_address"
                      label="Client Address *"
                      as="textarea"
                      disabled={isViewMode || sumbmitting}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FloatingInput
                      name="estimated_start_date"
                      label="Estimated Start Date *"
                      type="date"
                      disabled={isViewMode || sumbmitting}
                    />
                    <FloatingInput
                      name="estimated_end_date"
                      label="Estimated End Date *"
                      type="date"
                      disabled={isViewMode || sumbmitting}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {isViewMode ? (
                      <>
                        <FloatingInput
                          name="site_incharge"
                          label="Site Incharge"
                          disabled
                          value={
                            siteInchargeList.find(
                              (u) =>
                                String(u.id) === String(values.site_incharge)
                            )
                              ? `${
                                  siteInchargeList.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(values.site_incharge)
                                  )?.first_name
                                } ${
                                  siteInchargeList.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(values.site_incharge)
                                  )?.last_name
                                }`
                              : "N/A"
                          }
                        />

                        <FloatingInput
                          name="project_incharge"
                          label="Project Incharge"
                          disabled
                          value={
                            projectInchargeList.find(
                              (u) =>
                                String(u.id) === String(values.project_incharge)
                            )
                              ? `${
                                  projectInchargeList.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(values.project_incharge)
                                  )?.first_name
                                } ${
                                  projectInchargeList.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(values.project_incharge)
                                  )?.last_name
                                }`
                              : "N/A"
                          }
                        />
                      </>
                    ) : (
                      <>
                        <div className="relative">
                          <label className="block font-medium text-gray-700 mb-1">
                            Site Incharge *
                          </label>
                          <Field
                            as="select"
                            name="site_incharge"
                            disabled={sumbmitting}
                            className="w-full h-[44px] px-3 text-gray-700 text-sm border border-gray-300 rounded-md bg-white appearance-none focus:outline-none focus:ring-0 focus:border-secondary"
                          >
                            <option value="">Select Site Incharge</option>
                            {siteInchargeList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {`${user.first_name} ${user.last_name}`}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="site_incharge">
                            {(msg) => (
                              <div className="text-red-500 text-xs mt-1">
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>

                        <div className="relative">
                          <label className="block font-medium text-gray-700 mb-1">
                            Project Incharge *
                          </label>
                          <Field
                            as="select"
                            name="project_incharge"
                            disabled={sumbmitting}
                            className="w-full h-[44px] px-3 text-gray-700 text-sm border border-gray-300 rounded-md bg-white appearance-none focus:outline-none focus:ring-0 focus:border-secondary"
                          >
                            <option value="">Select Project Incharge</option>
                            {projectInchargeList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {`${user.first_name} ${user.last_name}`}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="project_incharge">
                            {(msg) => (
                              <div className="text-red-500 text-xs mt-1">
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </>
                    )}
                  </div>

                  {isHR && (
                    <div className="mt-4">
                      {isViewMode ? (
                        <FloatingInput
                          name="tag_option"
                          label="Tag Option"
                          disabled
                          value={
                            tagOptionsList.find(
                              (o) => String(o.id) === String(values.tag_option)
                            )
                              ? `${
                                  tagOptionsList.find(
                                    (o) =>
                                      String(o.id) === String(values.tag_option)
                                  )?.tag_name
                                } - ${
                                  tagOptionsList.find(
                                    (o) =>
                                      String(o.id) === String(values.tag_option)
                                  )?.tag_option_name
                                }`
                              : "N/A"
                          }
                        />
                      ) : (
                        <div className="relative">
                          <label className="block font-medium text-gray-700 mb-1">
                            Tag Option
                          </label>
                          <Field
                            as="select"
                            name="tag_option"
                            disabled={sumbmitting}
                            className="w-full h-[44px] px-3 text-gray-700 text-sm border border-gray-300 rounded-md bg-white appearance-none focus:outline-none focus:ring-0 focus:border-secondary"
                          >
                            <option value="">Select Tag Option</option>
                            {tagOptionsList.map((option) => (
                              <option key={option.id} value={option.id}>
                                {`${option.tag_name} - ${option.tag_option_name}`}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="tag_option">
                            {(msg) => (
                              <div className="text-red-500 text-xs mt-1">
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status field */}
                  <div className="mt-4">
                    {isViewMode ? (
                      <FloatingInput
                        name="is_active"
                        label="Status"
                        disabled
                        value={values.is_active ? "Active" : "Inactive"}
                      />
                    ) : (
                      <label className="flex items-center gap-2">
                        <Field
                          type="checkbox"
                          name="is_active"
                          className="w-4 h-4 text-blue-600 focus:ring-0 focus:border-secondary"
                          disabled={sumbmitting}
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    )}
                  </div>

                  {!isViewMode && (
                    <div className="space-y-1 mt-4">
                      <label className="block font-medium text-gray-700">
                        Search Location
                      </label>
                      <StandaloneSearchBox
                        onLoad={(ref) => (searchBoxRef.current = ref)}
                        onPlacesChanged={handlePlacesChanged}
                      >
                        <input
                          type="text"
                          placeholder="Search address in India..."
                          className="w-full px-4 py-2 rounded border-gray-300"
                        />
                      </StandaloneSearchBox>
                    </div>
                  )}
                </div>

                {isHR && (
                  <aside className="w-full lg:w-64 shrink-0">
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 lg:sticky lg:top-4">
                      <label className="block font-semibold text-gray-700 mb-2">
                        Assigned Users
                      </label>

                      {isViewMode ? (
                        (() => {
                          const assignedInfo =
                            initialValues?.assigned_users_info || [];
                          const visible = showAllSelected
                            ? assignedInfo
                            : assignedInfo.slice(
                                0,
                                SELECTED_USERS_PREVIEW_COUNT
                              );
                          const remaining =
                            assignedInfo.length - visible.length;
                          return assignedInfo.length > 0 ? (
                            <div className="grid grid-cols-2 gap-1.5">
                              {visible.map((u) => (
                                <span
                                  key={u.id}
                                  className="flex items-center gap-1.5 min-w-0 bg-white border border-gray-200 rounded-full pl-1 pr-2 py-1 text-xs text-gray-700"
                                >
                                  <span className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                                    {getUserInitials(u)}
                                  </span>
                                  <span className="truncate min-w-0">
                                    {`${u.first_name} ${u.last_name}`}
                                  </span>
                                </span>
                              ))}
                              {remaining > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setShowAllSelected(true)}
                                  className="col-span-2 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full px-2 py-1 text-xs"
                                >
                                  {`+${remaining} more`}
                                  <FiChevronDown size={12} />
                                </button>
                              )}
                              {showAllSelected &&
                                remaining === 0 &&
                                assignedInfo.length >
                                  SELECTED_USERS_PREVIEW_COUNT && (
                                  <button
                                    type="button"
                                    onClick={() => setShowAllSelected(false)}
                                    className="col-span-2 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full px-2 py-1 text-xs"
                                  >
                                    Show less
                                    <FiChevronUp size={12} />
                                  </button>
                                )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              No users assigned
                            </p>
                          );
                        })()
                      ) : (
                        <>
                          <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            disabled={sumbmitting}
                            placeholder="Search users..."
                            className="w-full h-9 px-3 mb-2 text-sm text-gray-700 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-0 focus:border-secondary"
                          />

                          {values.assigned_users.length > 0 &&
                            (() => {
                              const selectedUserObjects = allUsersList.filter(
                                (u) => values.assigned_users.includes(u.id)
                              );
                              const visible = showAllSelected
                                ? selectedUserObjects
                                : selectedUserObjects.slice(
                                    0,
                                    SELECTED_USERS_PREVIEW_COUNT
                                  );
                              const remaining =
                                selectedUserObjects.length - visible.length;
                              return (
                                <div className="grid grid-cols-2 gap-1.5 mb-2 pb-2 border-b border-gray-200">
                                  {visible.map((u) => (
                                    <span
                                      key={u.id}
                                      className="flex items-center gap-1 min-w-0 bg-primary/10 text-primary border border-primary/20 rounded-full pl-1 pr-1.5 py-0.5 text-xs"
                                    >
                                      <span className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                                        {getUserInitials(u)}
                                      </span>
                                      <span className="min-w-0 flex-1 truncate">
                                        {`${u.first_name} ${u.last_name}`}
                                      </span>
                                      <button
                                        type="button"
                                        disabled={sumbmitting}
                                        onClick={() =>
                                          setFieldValue(
                                            "assigned_users",
                                            values.assigned_users.filter(
                                              (id) => id !== u.id
                                            )
                                          )
                                        }
                                        className="hover:text-red-600 shrink-0"
                                        aria-label={`Remove ${u.first_name} ${u.last_name}`}
                                      >
                                        <FiX size={12} />
                                      </button>
                                    </span>
                                  ))}
                                  {remaining > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setShowAllSelected(true)}
                                      className="col-span-2 flex items-center justify-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full px-2 py-0.5 text-xs"
                                    >
                                      {`+${remaining} more`}
                                      <FiChevronDown size={12} />
                                    </button>
                                  )}
                                  {showAllSelected &&
                                    remaining === 0 &&
                                    selectedUserObjects.length >
                                      SELECTED_USERS_PREVIEW_COUNT && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowAllSelected(false)
                                        }
                                        className="col-span-2 flex items-center justify-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full px-2 py-0.5 text-xs"
                                      >
                                        Show less
                                        <FiChevronUp size={12} />
                                      </button>
                                    )}
                                </div>
                              );
                            })()}

                          <div className="max-h-64 overflow-y-auto space-y-0.5">
                            {filteredUsersList.length === 0 && (
                              <p className="text-sm text-gray-500 px-1 py-1">
                                {allUsersList.length === 0
                                  ? "No users available"
                                  : "No users found"}
                              </p>
                            )}
                            {filteredUsersList.map((u) => {
                              const selected = values.assigned_users.includes(
                                u.id
                              );
                              return (
                                <button
                                  type="button"
                                  key={u.id}
                                  disabled={sumbmitting}
                                  onClick={() => {
                                    const next = selected
                                      ? values.assigned_users.filter(
                                          (id) => id !== u.id
                                        )
                                      : [...values.assigned_users, u.id];
                                    setFieldValue("assigned_users", next);
                                  }}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left ${
                                    selected
                                      ? "bg-primary/10"
                                      : "hover:bg-gray-100"
                                  }`}
                                >
                                  <span className="w-6 h-6 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                                    {getUserInitials(u)}
                                  </span>
                                  <span className="truncate flex-1">
                                    {`${u.first_name} ${u.last_name}`}
                                  </span>
                                  {selected && (
                                    <span className="text-primary text-xs shrink-0">
                                      ✓
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </aside>
                )}
              </div>

              <div className="space-y-1 my-4">
                <label className="block font-medium text-gray-700">
                  Geofence
                </label>
                <div className="relative border rounded shadow overflow-hidden">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={polygonPath[0] || center}
                    zoom={polygonPath.length ? 16 : 12}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                    options={{
                      scrollwheel: false,
                      draggableCursor: "crosshair",
                    }}
                    onClick={(e) => {
                      if (
                        mode === constants.EDIT_MODE ||
                        mode === constants.ADD_MODE
                      ) {
                        const lat = e.latLng.lat();
                        const lng = e.latLng.lng();
                        setPolygonPath((prev) => [...prev, { lat, lng }]);
                      }
                    }}
                  >
                    {polygonPath.length > 0 && (
                      <Polygon
                        path={polygonPath}
                        options={{
                          fillColor: "#2196f3",
                          fillOpacity: 0.3,
                          strokeColor: "#2196f3",
                          strokeWeight: 2,
                          editable: false, // no dragging
                        }}
                      />
                    )}

                    {polygonPath.map((point, index) => (
                      <Marker
                        key={index}
                        position={point}
                        draggable={false}
                        icon={{
                          path: "M 0,0 m -2,0 a 2,2 0 1,0 4,0 a 2,2 0 1,0 -4,0", // circle
                          fillColor: "#ff0000",
                          fillOpacity: 1,
                          strokeColor: "#ffffff",
                          strokeWeight: 1,
                          scale: 2,
                        }}
                      />
                    ))}
                  </GoogleMap>

                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setPolygonPath([]);
                        setPolygonData(null);
                      }}
                      className="absolute flex items-center gap-1 top-3 left-1/2 -translate-x-1/2 bg-white hover:bg-gray-200 text-gray-600 px-3 py-2 rounded border-2 border-slate-600 z-10 text-lg"
                    >
                      <FiTrash2 />
                      <span className="text-xs font-semibold">
                        Clear Polygon
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {!isViewMode && (
                <div className="flex justify-between gap-3 pt-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => navigate(routes.SITE)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-semibold text-base"
                    disabled={sumbmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-secondary text-white px-6 py-2 rounded font-semibold text-base"
                  >
                    {submitText}
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default SiteForm;
